import chroma from 'chroma-js'
import { colors, getColorMap } from '~/design'
import { Layer } from '~/packages/general/variable/spaceTime.types'
import { Info } from './geoAndStatsToFeatureCollection'
import { VariableAndOptionalResult } from '~/packages/general/variable/variables.typing'

export type ValueType = 'count' | 'percentage'

export interface StyleForLayer {
  mapStyle: (info: { value?: number }) => {
    fillColor: string
    strokeColor: string
    strokeWeight: number
    fillOpacity: number
    strokeOpacity?: number
    zIndex: number
  }
  valueType: ValueType
  colorMap: string[]
  min: number
  max: number
  total: number
  totalNumerator: number
  totalDenominator: number
}

export default function styleForLayer(
  layer: Layer,
  {
    minValue,
    maxValue,
    totalValue,
    totalNumeratorValue,
    totalDenominatorValue,
    fillOpacity = 0.7,
    strokeOpacity = 1,
  }: {
    minValue: number
    maxValue: number
    totalValue: number
    totalNumeratorValue: number
    totalDenominatorValue: number
    fillOpacity?: number
    strokeOpacity?: number
  },
): StyleForLayer {
  const valueType: ValueType = (layer.variable as VariableAndOptionalResult).highlight
    ? 'percentage'
    : 'count'

  let min: number
  let max: number
  let total: number
  let totalNumerator: number
  let totalDenominator: number

  if (valueType === 'percentage' && false) {
    min = 0
    max = 100
  } else {
    min = minValue
    max = maxValue
    total = totalValue
    totalNumerator = totalNumeratorValue
    totalDenominator = totalDenominatorValue
  }

  const colorMap =
    layer.space.colorMap ??
    getColorMap((layer.variable as VariableAndOptionalResult).betterDensity ?? 'less', 'choropleth')
  const colorMapFunction = chroma.scale(colorMap).mode('lrgb')

  const isHeatmap =
    layer.space.childrenType === 'item' && layer.space.childrenItemView === 'heatmap'

  const mapStyle = ({ value }: Partial<Info>) => {
    if (value === undefined) {
      return borderOnlyStyle.mapStyle()
    }

    const ratio = max === min ? (min ? 1 : 0) : (value - min) / (max - min)
    const chromaColor = colorMapFunction(ratio)
    const fillColor = isHeatmap ? 'RGBA(0,0,0,0)' : chromaColor.hex()
    const strokeColor = isHeatmap ? 'RGBA(0,0,0,0)' : chromaColor.darken().hex()

    const zIndex = Math.round(ratio * 100)

    return {
      fillColor,
      strokeColor,
      strokeWeight: 3,
      strokeOpacity,
      fillOpacity,
      zIndex,
    }
  }

  return {
    mapStyle,
    valueType,
    colorMap,
    min,
    max,
    total,
    totalNumerator,
    totalDenominator,
  }
}

export const borderOnlyStyle = {
  mapStyle: () => ({
    fillColor: '#000000',
    fillOpacity: 0,
    strokeColor: colors.prixOrange,
    strokeWeight: 2,
    strokeOpacity: 0.8,
    zIndex: 0,
  }),
  colorMap: [colors.prixOrange],
  valueType: 'count' as ValueType,
  min: 0,
  max: 0,
  total: 0,
  totalNumerator: 0,
  totalDenominator: 0,
}
